import { isEqual, omit } from 'lodash'
import React from 'react'
import { connect } from 'react-redux'
import Actions from '../../actions'
import { showToast } from '../../common/utils'
import ConfirmationDialog from '../../LEGACY/ConfirmationDialog'
import Operations from '../../operations'
import { history } from '../../store'
import { withRouter } from '../../withRouter'

interface Props {
  addFormFile: () => void
  router: {
    location: {
      pathname: string
    }
  }
  clear: () => void
  errors: string[]
  fetchList: () => void
  isModal: boolean
  isOpen: boolean
  isSubmitting: boolean
  updateItem: () => void
  createItem: () => void
  remove: () => void
  setFormField: () => void
  setFormState: () => void
  user: Record<string, unknown>
  validationErrors: Record<string, unknown>
}

interface State {
  removeDialogOpen: boolean
  removeActions?: () => void
}

let unblock: any

export default function Form(Form: React.ComponentType<any>, formId: string) {
  class WrappedForm extends React.Component<Props, State> {
    state: State = {
      removeDialogOpen: false
    }

    componentDidUpdate() {
      const {
        // @ts-ignore
        fields,
        // @ts-ignore
        initial,
        router: {
          location: { pathname }
        }
      } = this.props
      if (isEqual(fields, initial)) {
        unblock?.()
        unblock = undefined
      } else if (
        // Only block navigation if we're on the logs page.
        !isEqual(fields, initial) &&
        pathname.startsWith('/logs/') &&
        !this.state.removeDialogOpen
      ) {
        if (!unblock) {
          unblock = history.block((tx: any) => {
            // Navigation was blocked! Let's update state and show a confirmation dialog
            // so the user can decide if they actually want to navigate
            // away and discard changes they've made in the current page.
            this.setState({
              removeActions: () => {
                unblock() // Unblock the navigation.
                tx.retry() // Retry the transition.
              },
              removeDialogOpen: true
            })
          })
        }
      }
    }

    componentWillUnmount() {
      unblock = undefined
    }

    checkIfUserWillLeavePage = () => {
      // @ts-ignore
      const { fields, initial } = this.props
      if (!isEqual(fields, initial) && !this.state.removeDialogOpen) {
        return REMOVE_CONFIRM_MESSAGE
      }
    }

    checkIfUserWillRefresh = (e: any) => {
      // @ts-ignore
      const { fields, initial } = this.props
      if (!isEqual(fields, initial) && !this.state.removeDialogOpen) {
        e.returnValue = 'Please save changes before reloading the page.'
      }
    }

    handleLeavePage = (onConfirm: () => void) => {
      // @ts-ignore
      const { fields, initial } = this.props
      if (!isEqual(fields, initial)) {
        this.setState({
          removeActions: onConfirm,
          removeDialogOpen: true
        })
      } else {
        onConfirm()
      }
    }

    handleRemove = ({ onConfirm }: { onConfirm: () => void }) => {
      // @ts-ignore
      const { fields, initial, remove } = this.props
      if (!isEqual(fields, initial)) {
        this.setState({
          removeActions: onConfirm,
          removeDialogOpen: true
        })
      } else {
        if (onConfirm) onConfirm()
        remove()
      }
    }

    cancelRemove = () => {
      this.setState({
        removeDialogOpen: false
      })
    }

    confirmRemove = () => {
      this.setState({
        removeDialogOpen: false
      })
      unblock?.()
      this.props.remove()
      if (this.state.removeActions) {
        this.state.removeActions()
      }
    }

    render() {
      return (
        <div>
          <Form
            remove={this.handleRemove}
            showToast={showToast}
            onCancel={this.handleLeavePage}
            {...omit(this.props, 'remove')}
          />
          <ConfirmationDialog
            confirmationLabel="Leave"
            confirmationType="warning"
            isOpen={this.state.removeDialogOpen}
            onCancel={() => this.cancelRemove()}
            onConfirmation={() => this.confirmRemove()}
          >
            {REMOVE_CONFIRM_MESSAGE}
          </ConfirmationDialog>
        </div>
      )
    }
  }

  const REMOVE_CONFIRM_MESSAGE =
    'Are you sure you want to leave the page? Any changes you made will be lost.'

  const mapStateToProps = (state: any) => {
    return {
      ...state.forms[formId],
      user: state.global.user
    }
  }

  const mapDispatchToProps = (dispatch: any) => {
    return {
      addForm: (initial: any) => dispatch(Actions.addForm({ initial, formId })),
      addFormFile: ({ key, file, uri }: any) =>
        dispatch(Actions.addFormFile({ key, file, uri, formId })),
      clear: () => dispatch(Actions.clearForm(formId)),
      remove: () => dispatch(Actions.removeForm(formId)),
      fetchList: (list: any, opts: any) =>
        dispatch(Operations.loadList(list, opts)),
      setLoadedListItem: (list: any, item: any) =>
        dispatch(Actions.setLoadedListItem(list, item)),
      updateItem: (data: any) =>
        dispatch(Operations.postItem({ ...data, formId, method: 'PUT' })),
      createItem: (data: any) =>
        dispatch(Operations.postItem({ ...data, formId, method: 'POST' })),
      setFormField: ({ key, value }: any) =>
        dispatch(Actions.setFormField({ key, value, formId })),
      setFormState: ({ key, value }: any) =>
        dispatch(Actions.setFormState({ key, value, formId }))
    }
  }

  return connect(mapStateToProps, mapDispatchToProps)(withRouter(WrappedForm))
}
